import {
  Row, Col, message, Button, Form, Input,
  Select, Breadcrumb, Upload, Spin, Space,
} from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { routes } from "common/routes";
import { submitPurchaseCodeAmendment } from "store/purchase-codes/purchaseCodesSlice";
import { AUTH_HEADER_NAME, apis } from "common/apis";
import { COMPANY_TAG_OPTIONS } from "common/constants";
import styled from "styled-components";

const MESSAGE_KEY = "purchase_code_edit";

const UOM_OPTIONS = [
  { value: "KILOGRAM", label: "Kilogram" },
  { value: "PACKET", label: "Packet" },
  { value: "BUNDLE", label: "Bundle" },
  { value: "NUMBER", label: "Number" },
  { value: "BOX", label: "Box" },
];

// Fallback categories when the init config has not loaded skuCategories yet
const FALLBACK_CATEGORIES = [
  "Fruits",
  "Vegetables",
  "Exotic",
  "Spreads",
  "Dairy",
  "Flowers",
  "Spices",
  "Sweetners",
  "Soaked",
  "Kitchen"
];

const ImageUploadBox = styled.div`
  width: 160px;
  height: 160px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  gap: 0.5rem;
  color: #888;
  transition: border-color 0.2s;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
`;

const ImagePreview = styled.img`
  width: 160px;
  height: 160px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  display: block;
`;

const PurchaseCodeEditPage = () => {
  useDocumentTitle("Create Purchase Code");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { amendmentStatus, amendmentError } = useAppSelector((s) => s.purchaseCodes);
  const { authToken } = useAppSelector((state) => state.app.user);
  const skuCategories = useAppSelector((state) => state.app.initConfig?.skuCategories);

  // Categories come from the global init config (skuCategories)
  const categoryOptions = (skuCategories?.length ? skuCategories : FALLBACK_CATEGORIES)
    .map((c) => ({ value: c, label: c }));

  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);

  const uploadHeaders: Record<string, string> = authToken ? { [AUTH_HEADER_NAME]: authToken } : {};

  useEffect(() => {
    if (amendmentStatus === "error" && amendmentError) {
      message.error({ content: amendmentError, key: MESSAGE_KEY });
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [amendmentStatus, amendmentError]);

  const handleImageChange = (info: any) => {
    if (info.file.status === "uploading") {
      setUploading(true);
    } else if (info.file.status === "done") {
      setUploading(false);
      const url = info.file.response?.url;
      if (url) {
        setImageUrl(url);
        form.setFieldsValue({ image: url });
      }
    } else if (info.file.status === "error") {
      setUploading(false);
      message.error({ content: "Image upload failed", key: MESSAGE_KEY });
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(undefined);
    form.setFieldsValue({ image: undefined });
  };

  const buildMetadata = (values: any) => {
    return {
      ...(values.companyTag != null ? { companyTag: values.companyTag } : {}),
    };
  };

  const handleSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const result = await dispatch(
      submitPurchaseCodeAmendment({
        entityType: "PURCHASE_SKU",
        requestType: "CREATE",
        payload: {
          skuCode: values.skuCode,
          name: values.name,
          image: imageUrl,
          category: values.category,
          unit_of_measurement: values.unit_of_measurement,
          metadata: buildMetadata(values),
        },
      })
    );

    if (submitPurchaseCodeAmendment.fulfilled.match(result)) {
      message.success({ content: "Purchase Code create request submitted for approval!", key: MESSAGE_KEY });
      navigate(`/${routes.PO.PURCHASE_CODE_AMENDMENTS}`);
    }
  };

  return (
    <>
      <Row style={{ marginBottom: "1rem" }}>
        <Col xs={24}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <NavLink to={`/${routes.PO.PURCHASE_CODES}`}>Purchase Codes</NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Create</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>

      <Row>
        <Col>
          <AppPageHeader title="Create Purchase Code" />
        </Col>
      </Row>

      <Form form={form} layout="vertical" style={{ maxWidth: 860 }}>
        <Form.Item name="image" hidden>
          <Input />
        </Form.Item>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="skuCode"
              label="SKU Code"
              rules={[{ required: true, message: "Please enter a SKU Code" }]}
            >
              <Input placeholder="e.g. PS012" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter a name" }]}
            >
              <Input placeholder="e.g. Test Purchase SKU" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select placeholder="Select category" options={categoryOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="unit_of_measurement"
              label="Unit of Measurement"
              rules={[{ required: true, message: "Please select a unit" }]}
            >
              <Select placeholder="Select unit" options={UOM_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="companyTag"
              label="Company Tag"
              rules={[{ required: true, message: "Please select a Company Tag" }]}
            >
              <Select placeholder="Select company tag" options={COMPANY_TAG_OPTIONS} allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="center" style={{ marginTop: "1.5rem" }}>
          <Col style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "0.5rem", fontWeight: 500, color: "rgba(0,0,0,0.85)" }}>
              Image
            </div>
            {imageUrl ? (
              <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "0.6rem" }}>
                <ImagePreview src={imageUrl} alt="Preview" />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={handleRemoveImage}>
                  Remove
                </Button>
              </div>
            ) : (
              <Upload
                name="file"
                showUploadList={false}
                action={apis.uploadVendorImage}
                headers={uploadHeaders}
                accept="image/*"
                onChange={handleImageChange}
              >
                <ImageUploadBox>
                  {uploading ? (
                    <Spin />
                  ) : (
                    <>
                      <UploadOutlined style={{ fontSize: 24 }} />
                      <span style={{ fontSize: 12 }}>Click to upload</span>
                    </>
                  )}
                </ImageUploadBox>
              </Upload>
            )}
          </Col>
        </Row>

        <Row justify="center" style={{ marginTop: "2rem" }}>
          <Col>
            <Space size="middle">
              <Button onClick={() => navigate(`/${routes.PO.PURCHASE_CODES}`)}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={amendmentStatus === "saving"}
                disabled={amendmentStatus === "saving"}
              >
                Submit Create Request
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default PurchaseCodeEditPage;
